import * as I18nAPI from '../api/i18n';
import * as MatchAPI from '../api/match';
import * as RequestParams from '../config/RequestParams';
const MINUTE_SYMBOL = String.fromCharCode(8242);
const SECTION_FORMAT = /([A-Z]+)([0-9]*)/;
/**
 * Formats the date of a match
 * @param {string} dateString
 */
export function formatMatchDate(dateString, language) {
    const date = new Date(dateString);
    const isSameDate = new Date().toISOString().substr(0, 10) === date.toISOString().substr(0, 10);
    if (isSameDate) {
        return I18nAPI.translate('common', 'date-today');
    }
    else {
        const dateOptions = { weekday: 'short', month: 'numeric', day: 'numeric' };
        const dateStr = new Intl.DateTimeFormat(RequestParams.getLocaleForLanguage(language), dateOptions).format(date);
        return dateStr;
    }
}
export function formatSimpleDate(dateString, language) {
    if (!dateString) {
        return '';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
        return '';
    }
    const dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    return new Intl.DateTimeFormat(RequestParams.getLocaleForLanguage(language), dateOptions).format(date);
}
/**
 * Format time of match
 * @param {string} dateString
 */
export function formatMatchTime(dateString, language) {
    const dateOptions = { hour: 'numeric', minute: 'numeric' };
    const date = new Intl.DateTimeFormat(RequestParams.getLocaleForLanguage(language), dateOptions).format(new Date(dateString));
    return date;
}
function getAdditionalTime(playingTime) {
    if (playingTime.expected > 0)
        return Math.max(playingTime.current - playingTime.expected, 0);
    else
        return 0;
}
function getRegularTime(playingTime) {
    return getAdditionalTime(playingTime) > 0 ? playingTime.expected : playingTime.current;
}
function toMinutes(seconds) {
    return Math.ceil(seconds / 60);
}
export function getPlayingTimeOrSection(sport, contestStatus) {
    if (contestStatus.name !== 'Running')
        return null;
    if (sport === 'Football' && contestStatus.playingTime) {
        const regularTime = getRegularTime(contestStatus.playingTime);
        const additionalTime = getAdditionalTime(contestStatus.playingTime);
        if (additionalTime > 0) {
            return `${toMinutes(regularTime)}${MINUTE_SYMBOL}+${toMinutes(additionalTime)}`;
        }
        else {
            return `${toMinutes(regularTime)}${MINUTE_SYMBOL}`;
        }
    }
    else {
        const parsedSection = SECTION_FORMAT.exec(contestStatus.section);
        if (parsedSection) {
            const section = parsedSection[1];
            const index = parsedSection[2];
            return I18nAPI.translate('common', `sectionTypes-${section}`, { index });
        }
        else {
            return contestStatus.section;
        }
    }
}
export function getScore(contestStatus, scores, atMode = false) {
    if (contestStatus.name === MatchAPI.ContestStatusName.Pregame)
        return null;
    let matchScore = scores.find(score => score.name === 'Match');
    if (!matchScore)
        return null;
    let score = matchScore.score;
    if (atMode) {
        score = [score[1], score[0]];
    }
    return score.join(':');
}
export function getTennisScores(scores, filter = '') {
    return scores.filter(score => score.name !== 'Match' && score.name.startsWith(filter));
}
export function getSectionScores(contestStatus, scores, filter = '') {
    if (contestStatus.name === MatchAPI.ContestStatusName.Pregame)
        return null;
    return scores.filter(score => score.name !== 'Match' && score.name.startsWith(filter));
}
export function isMatchRunning(contestStatus) {
    return contestStatus && contestStatus.name === MatchAPI.ContestStatusName.Running;
}
export function getBetmarketOfType(betmarkets, bettingType) {
    var _a;
    if (!bettingType) {
        return ((_a = getBetmarketOfType(betmarkets, MatchAPI.BettingType.OddsBoost)) !== null && _a !== void 0 ? _a : getBetmarketOfType(betmarkets, MatchAPI.BettingType.MatchWin));
    }
    return betmarkets.find(betmarket => betmarket.bettingType === bettingType) || null;
}
export function getFilteredMatches(matches, requestParams) {
    return matches.reduce((result, match) => {
        if (requestParams.autoFill === 'false') {
            if (requestParams.sport && match.sport !== requestParams.sport)
                return result;
            if (requestParams.region && match.region !== requestParams.region)
                return result;
            if (requestParams.league && match.league !== requestParams.league)
                return result;
            if (requestParams.team &&
                match.home &&
                match.away &&
                match.home !== requestParams.team &&
                match.away !== requestParams.team)
                return result;
        }
        const originalBetmarket = getBetmarketOfType(match.betmarkets, requestParams.market);
        if (originalBetmarket) {
            let betmarket;
            if (requestParams.outrightOutcome && originalBetmarket.bettingType === MatchAPI.BettingType.Place) {
                betmarket = Object.assign(Object.assign({}, originalBetmarket), { odds: originalBetmarket.odds.filter(odds => odds.outcome === requestParams.outrightOutcome) });
            }
            else {
                betmarket = originalBetmarket;
            }
            if (betmarket.odds.length > 0) {
                const newMatch = Object.assign(Object.assign({}, match), { betmarkets: [betmarket] });
                if (requestParams.hideLiveScores === 'true' && isMatchRunning(newMatch.contestStatus)) {
                    newMatch.contestStatus = { name: MatchAPI.ContestStatusName.Pregame };
                }
                result.push(newMatch);
            }
        }
        return result;
    }, []);
}
export function getThreeWayOdds(odds, atMode) {
    if (atMode) {
        return odds.concat([]).reverse();
    }
    else {
        return odds;
    }
}
export function getTeamType(teamType, atMode) {
    switch (teamType) {
        case 'home':
            return atMode ? 'away' : 'home';
        case 'away':
            return atMode ? 'home' : 'away';
    }
}
export function getTeams(match) {
    const teams = [
        {
            type: 'home',
            name: match.home,
            label: match.homeLabel,
            jerseys: match.homeJerseys
        },
        {
            type: 'away',
            name: match.away,
            label: match.awayLabel,
            jerseys: match.awayJerseys
        }
    ];
    if (match.atMode) {
        return teams.slice().reverse();
    }
    else {
        return teams;
    }
}
export function isEsport(sport) {
    switch (sport) {
        case MatchAPI.Sport.CallOfDuty:
        case MatchAPI.Sport.CounterStrikeGO:
        case MatchAPI.Sport.DefenseOfTheAncients:
        case MatchAPI.Sport.LeagueOfLegends:
        case MatchAPI.Sport.Overwatch:
            return true;
        default:
            return false;
    }
}
//# sourceMappingURL=MatchFormatter.js.map